// Weddings.jsx
import { useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import "./Weddings.css";

const NAV_OFFSET = 86;

// Works for Vite (VITE_*) and CRA (REACT_APP_*)
const API_BASE =
  (typeof import.meta !== "undefined" && import.meta.env?.VITE_CONTACT_API_URL) ||
  process.env.REACT_APP_CONTACT_API_URL ||
  "https://xjref277ki.execute-api.us-west-2.amazonaws.com";

// If your API expects a path like "/contact" or "/prod/contact", change it here:
const CONTACT_ENDPOINT = API_BASE;

const PriceCard = ({ title, price, desc, selected, onToggle }) => (
  <div className={`wed-card ${selected ? "is-selected" : ""}`}>
    <div className="wed-cardTop">
      <h3 className="wed-cardTitle">{title}</h3>
      <div className="wed-cardPrice">{price}</div>
    </div>
    <p className="wed-cardDesc">{desc}</p>

    <button
      type="button"
      className={`psoc-btn ${selected ? "psoc-btnPrimary" : "psoc-btnSecondary"} wed-cardBtn`}
      onClick={onToggle}
    >
      {selected ? "Added" : "Add to inquiry"}
    </button>
  </div>
);

const Weddings = () => {
  const contactRef = useRef(null);

  const [selected, setSelected] = useState(() => new Set());
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState({ state: "idle", message: "" }); // idle | sending | success | error
  const [subject, setSubject] = useState("Wedding Videography Inquiry");

  const services = useMemo(
    () => [
      {
        title: "Proposal Video",
        price: "$500",
        desc: "Capture the moment and reactions with clean audio and a cinematic edit.",
      },
      {
        title: "Rehearsal Dinner",
        price: "$550",
        desc: "A relaxed, story-forward add-on that complements your wedding film.",
      },
      {
        title: "Pre-Ceremony",
        price: "$1,200",
        desc: "Getting ready coverage with details, candid moments, and atmosphere.",
      },
      {
        title: "Ceremony",
        price: "$950",
        desc: "Full ceremony coverage with intentional angles and clear audio capture.",
      },
      {
        title: "Reception",
        price: "$1,600",
        desc: "Grand entrances, dances, speeches, energy. Edited for pacing and emotion.",
      },
      {
        title: "Video Booth",
        price: "$450",
        desc: "Fun guest messages and candid moments, delivered as a highlight reel.",
      },
    ],
    []
  );

  const faqs = useMemo(
    () => [
      {
        q: "Is it possible to videotape everyone at the wedding ceremony & reception?",
        a: "Unfortunately, it’s impossible to guarantee everyone will be filmed. If there are specific people you want prioritized, tell us ahead of time so we can plan coverage intentionally.",
      },
      {
        q: "My photographer has an hourly limit. Do you?",
        a: "Nope. We’re there for the day. From the first scheduled moment until the last.",
      },
      {
        q: "Can we add a Videobooth on the wedding day?",
        a: "Yes. We can agree verbally and update the contract afterward.",
      },
      {
        q: "Can we add extra coverage on the day of?",
        a: "Yes. Same approach. We can update the contract after the wedding.",
      },
      {
        q: "How do you edit our video?",
        a: "We use cinematic techniques and condense the day into a film that feels intentional, emotional, and easy to rewatch.",
      },
      {
        q: "Can we buy the unedited footage?",
        a: "Yes. It’s $80 for a small drive. We try to keep your footage backed up for a year.",
      },
      {
        q: "How long does editing take? Can we request changes?",
        a: "We typically take 10–12 weeks. Minor edits are free. Larger changes that exceed 4 hours of editing are $75/hr.",
      },
      {
        q: "How much is the retainer & when is payment due?",
        a: "$450 retainer upfront, $450 on wedding day, and the remaining balance on delivery.",
      },
      {
        q: "What payment methods do you accept?",
        a: "Cash, check, ACH, and major credit/debit cards.",
      },
      {
        q: "Can we choose the music?",
        a: "We license music from independent artists. For first dances and key moments, we typically use original audio so it feels authentic.",
      },
      {
        q: "How do we get our video?",
        a: "We can ship via FedEx/UPS/USPS or arrange pickup when it’s ready.",
      },
      {
        q: "Are the DVDs and Blu-rays compatible?",
        a: "Almost always. If there’s ever a playback issue, we’ll provide a USB copy.",
      },
      {
        q: "Do you charge travel fees?",
        a: "Within about an hour of Denver is free. Beyond that, we ask for a reasonable travel fee.",
      },
    ],
    []
  );

  const scrollToContact = (presetSubject) => {
    if (presetSubject) setSubject(presetSubject);

    if (!contactRef.current) return;
    const y = contactRef.current.getBoundingClientRect().top + window.scrollY - NAV_OFFSET;
    window.scrollTo({ top: y, behavior: "smooth" });
  };

  const toggleService = (title) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(title)) next.delete(title);
      else next.add(title);

      const picked = Array.from(next);
      if (picked.length) setSubject(`Wedding Videography: ${picked.join(" + ")}`);
      else setSubject("Wedding Videography Inquiry");

      return next;
    });
  };

  const safeJson = async (res) => {
    const text = await res.text();
    try {
      return text ? JSON.parse(text) : {};
    } catch {
      return { message: text };
    }
  };

  const buildSelectedText = () => {
    const picked = Array.from(selected);
    if (!picked.length) return "";
    return `Selected services:\n- ${picked.join("\n- ")}\n\n`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    const form = e.currentTarget;

    const formData = {
      name: form.name.value.trim(),
      email: form.email.value.trim(),
      phone: form.phone.value.trim(),
      subject: subject?.trim() || "Wedding Videography Inquiry",
      message: `${buildSelectedText()}${form.message.value.trim()}`,
      sourcePage: "Weddings",
    };

    setSubmitting(true);
    setStatus({ state: "sending", message: "Sending…" });

    try {
      const res = await fetch(CONTACT_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      const data = await safeJson(res);

      if (!res.ok) {
        const msg = data?.message || "There was an issue submitting the form.";
        throw new Error(msg);
      }

      setStatus({ state: "success", message: data?.message || "Sent! We’ll get back to you soon." });
      form.reset();
      setSelected(new Set());
      setSubject("Wedding Videography Inquiry");
    } catch (err) {
      console.error(err);
      setStatus({
        state: "error",
        message: err?.message || "There was an error submitting the form.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="psoc wed">
      {/* Hero */}
      <section className="wed-hero">
        <div className="psoc-container wed-heroGrid">
          <div className="wed-heroText">
            <p className="psoc-badge">Weddings • Cinematic • All-day coverage</p>
            <h1 className="psoc-h1">Wedding Services</h1>
            <p className="psoc-lead">
              Pick only what you need. No pressure to buy more. We stay for your whole day, and deliver a film that feels
              emotional, clean, and intentional.
            </p>

            <div className="wed-highlights">
              <div className="wed-highlight">
                <div className="wed-highlightTop">No hourly limits</div>
                <div className="wed-highlightSub">We’re there from start to finish</div>
              </div>
              <div className="wed-highlight">
                <div className="wed-highlightTop">Simple payment</div>
                <div className="wed-highlightSub">$450 retainer, $450 day-of, balance on delivery</div>
              </div>
              <div className="wed-highlight">
                <div className="wed-highlightTop">Online access</div>
                <div className="wed-highlightSub">Includes 1 year of online access</div>
              </div>
            </div>

            <div className="wed-ctaRow">
              <button className="psoc-btn psoc-btnPrimary" type="button" onClick={() => scrollToContact("Wedding Quote")}>
                Get a Quote
              </button>
              <button className="psoc-btn psoc-btnSecondary" type="button" onClick={() => window.scrollTo({ top: window.innerHeight * 0.9, behavior: "smooth" })}>
                View Services
              </button>
            </div>

            <p className="wed-relatedLine">
              Also looking for <Link className="psoc-link" to="/video-production">video production</Link> or{" "}
              <Link className="psoc-link" to="/real-estate">real estate</Link>?
            </p>
          </div>

          <div className="wed-heroCard">
            <div className="wed-heroCardTitle">Quick quote checklist</div>
            <div className="wed-heroCardSub">Send these and we can price fast.</div>

            <div className="wed-chipRow">
              <span className="wed-chip">Date</span>
              <span className="wed-chip">Venue</span>
              <span className="wed-chip">Approx schedule</span>
              <span className="wed-chip">Coverage needed</span>
              <span className="wed-chip">Any must-film people</span>
            </div>

            <button className="psoc-btn psoc-btnSecondary wed-heroCardBtn" type="button" onClick={() => scrollToContact("Wedding Quote")}>
              Start with a message
            </button>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="psoc-section">
        <div className="psoc-container">
          <div className="psoc-sectionHead">
            <h2 className="psoc-h2">Additional Services</h2>
            <p className="psoc-sub">
              Tap to add items to your inquiry. Your selections will be included in the message automatically.
            </p>
          </div>

          <div className="wed-grid">
            {services.map((s) => (
              <PriceCard
                key={s.title}
                title={s.title}
                price={s.price}
                desc={s.desc}
                selected={selected.has(s.title)}
                onToggle={() => toggleService(s.title)}
              />
            ))}
          </div>

          {selected.size > 0 && (
            <div className="wed-selectedBar">
              <div className="wed-selectedTitle">Selected:</div>
              <div className="wed-selectedChips">
                {Array.from(selected).map((t) => (
                  <button key={t} type="button" className="wed-selectedChip" onClick={() => toggleService(t)} aria-label={`Remove ${t}`}>
                    {t} <span aria-hidden="true">×</span>
                  </button>
                ))}
              </div>
              <button className="psoc-btn psoc-btnPrimary" type="button" onClick={() => scrollToContact()}>
                Continue to contact
              </button>
            </div>
          )}
        </div>
      </section>

      {/* FAQ */}
      <section className="psoc-section psoc-sectionAlt">
        <div className="psoc-container">
          <div className="psoc-sectionHead">
            <h2 className="psoc-h2">Frequently Asked Questions</h2>
            <p className="psoc-sub">The stuff couples usually ask before booking.</p>
          </div>

          <div className="wed-faq">
            {faqs.map((f) => (
              <details key={f.q} className="wed-faqItem">
                <summary>{f.q}</summary>
                <p>{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Contact */}
      <section className="psoc-section" ref={contactRef} id="contact">
        <div className="psoc-container">
          <div className="psoc-sectionHead">
            <h2 className="psoc-h2">Contact Us</h2>
            <p className="psoc-sub">
              Tell us your date, venue, timeline, and what you want captured. We’ll respond by email.
            </p>
          </div>

          <div className="psoc-contactCard">
            <form onSubmit={handleSubmit} className="psoc-form">
              <div className="psoc-formGrid">
                <div className="psoc-field">
                  <label className="psoc-label" htmlFor="name">Name</label>
                  <input id="name" name="name" type="text" placeholder="Name" required autoComplete="name" />
                </div>

                <div className="psoc-field">
                  <label className="psoc-label" htmlFor="email">Email</label>
                  <input id="email" name="email" type="email" placeholder="Email" required autoComplete="email" />
                </div>

                <div className="psoc-field">
                  <label className="psoc-label" htmlFor="phone">Phone</label>
                  <input id="phone" name="phone" type="tel" placeholder="Phone (optional)" autoComplete="tel" />
                </div>

                <div className="psoc-field">
                  <label className="psoc-label" htmlFor="subject">Subject</label>
                  <input
                    id="subject"
                    name="subject"
                    type="text"
                    placeholder="Subject"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                  />
                </div>

                <div className="psoc-field psoc-fieldFull">
                  <label className="psoc-label" htmlFor="message">Message</label>
                  <textarea
                    id="message"
                    name="message"
                    placeholder="Date, venue, schedule, and what matters most to you (moments, people, style)…"
                    required
                    rows={7}
                  />
                </div>
              </div>

              <div className="psoc-formFooter">
                <button className="psoc-btn psoc-btnPrimary" type="submit" disabled={submitting}>
                  {submitting ? "Sending…" : "Submit"}
                </button>

                <div className="psoc-status" aria-live="polite">
                  {status.state !== "idle" && (
                    <span className={`psoc-statusMsg psoc-${status.state}`}>{status.message}</span>
                  )}
                </div>
              </div>
            </form>

            <p className="psoc-emailNote">
              Or email us at{" "}
              <a className="psoc-link" href="mailto:productionstudioofcolorado@gmail.com">
                productionstudioofcolorado@gmail.com
              </a>
            </p>
          </div>
        </div>
      </section>

      <footer className="psoc-footer">
        <div className="psoc-container psoc-footerInner">
          <span>© {new Date().getFullYear()} Production Studio of Colorado LLC</span>
          <button className="psoc-btn psoc-btnGhost" type="button" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}>
            Back to top
          </button>
        </div>
      </footer>
    </div>
  );
};

export default Weddings;
